import { HostListener, Injectable, OnDestroy, OnInit } from '@angular/core'
import { Graphics } from '../digital-twin/graphics/graphics'
import { GraphicsMaterials } from '../digital-twin/graphics/graphics-materials'
import { GraphicsRenderDecal } from '../digital-twin/graphics/graphics-render-decal'
import { WorkableElement } from '../platform/busines-data/SectionElements/WorkableElement'
import { AssetManager, FunctionalityMode } from './asset-manager'
import { SensorModel } from 'projects/phdsoft/common/src/lib/models/sensor.model'
import { BasicSensor } from '../platform/busines-data/SectionElements/SensorElements/BasicSensor'
import * as THREE from '@phdsoft/three'
import { PhdsoftCommonService, SESSION_STORAGE, StorageService, User, Util } from '@phdsoft/common'
import { IotMsgObject, IotRTSignalRService } from '../signalr/iot-rt-signalr'
import { Subject } from 'rxjs'
import { SensorStatusModel } from 'projects/phdsoft/common/src/lib/models/sensor-status.model'
import { SensorsService } from '../admin/services/sensors.service'
import { takeUntil } from 'rxjs/operators'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { Vector3 } from '@phdsoft/three'
import { Mesh } from '@phdsoft/three'
import { SensorAlarmModel } from 'projects/phdsoft/common/src/lib/models/sensor-alarm.model'
import { Lut } from 'src/app/digital-twin/Three/Lut.js';
import { SensorDataPair } from 'projects/phdsoft/common/src/lib/models/sensor-data-pair.model'
import { NotificationService } from '../signalr/notification.service'

@Injectable({ providedIn: 'root' })
export class AssetManagerSensors implements OnDestroy, OnInit {
  private sensorAlarmMap: Record<string, SensorAlarmModel>;
  private sensorMap: Record<string, SensorModel>;
  private resultBarMaxHeight: number = 50;
  private sensorReadingScale: Record<string, number>;
  lut: Lut;

  constructor(
    private graphicsRenderDecal: GraphicsRenderDecal,
    private graphics: Graphics,
    private assetManager: AssetManager,
    private serviceCommon: PhdsoftCommonService,
    private storageService: StorageService,
    private iotStreamService: IotRTSignalRService,
    private serviceSensor: SensorsService,
    private notificationService: NotificationService,
  ) {
    this.lut = new Lut('femscale');
  }

  async ngOnInit(): Promise<void> {
    await this.loadSensor3DModel();
  }

  private _sensorSelected: SensorModel

  public get sensorSelected(): SensorModel {
    return this._sensorSelected
  }
  public set sensorSelected(value: SensorModel) {
    this._sensorSelected = value
  }

  private get functionalityMode(): FunctionalityMode {
    return this.assetManager.functionalityMode
  }

  private set functionalityMode(value: FunctionalityMode) {
    this.assetManager.functionalityMode = value
  }

  private _listSensorsFiltered: SensorModel[] = []

  public get listSensorsFiltered(): SensorModel[] {
    return this._listSensorsFiltered
  }

  public set listSensorsFiltered(value: SensorModel[]) {
    this._listSensorsFiltered = value
  }


  private _rtReading : { [sensorId: string] : Subject<SensorDataPair>; } = {};

  public rtReadingSubject(sensorId : string) : Subject<SensorDataPair> {
    return this._rtReading[sensorId];
  }

  private _listSensors: SensorModel[] = []

  public get listSensors(): SensorModel[] {
    return this._listSensors
  }

  public set listSensors(value: SensorModel[]) {
    this._listSensors = value
  }

  private _sensor3dModel

  public get sensor3dModel() {
    return this._sensor3dModel
  }

  public set sensor3dModel(value) {
    this._sensor3dModel = value
  }

  private _loading = false

  public get loading() {
    return this._loading
  }

  public set loading(value) {
    this._loading = value
  }

  private _notFound = false

  public get notFound() {
    return this._notFound
  }

  public set notFound(value) {
    this._notFound = value
  }

  private _unsubscribe$ = new Subject<void>()

  public get unsubscribe$() {
    return this._unsubscribe$
  }

  public set unsubscribe$(value) {
    this._unsubscribe$ = value
  }

  private deviceMsg: Subject<IotMsgObject> = new Subject()
  private rawDeviceMsg: Subject<IotMsgObject> = new Subject()
  public listSensorStatus: SensorStatusModel[]

  @HostListener('unloaded')
  ngOnDestroy(): void {
    console.log('💣 Destroy AssetManagerSensors')
  }

  public startSensorsCollectionMode(): void {
    this.functionalityMode = 'SENSORINSERTION'
    this.graphics.Controls.enableRotate = false
  }

  public startSensorAdditionAction(ev, sectionId: string, regionIds: Array<string>): void {
    if (this.functionalityMode === 'SENSORINSERTION') {
      this.insertSensorMethod(ev, sectionId, regionIds)
    }
  }

  public async loadSensor3DModel(): Promise<void> {
    let sensorModelPath = null
    let gltf = null

    sensorModelPath = await this.serviceSensor.getSensorModel().toPromise()

    if (sensorModelPath) {
      const loader = new GLTFLoader()
      gltf = await loader.loadAsync(sensorModelPath)
    }

    if (gltf) {
      this.sensor3dModel = gltf.scene.children[0].children[0].children[0] as Mesh
      this.sensor3dModel.scale.set(0.025, 0.025, 0.025)
      this.sensor3dModel.rotateZ(-1.5708)
    }
  }

  loadSensors(): void {
    this.loading = true

    this.notFound = false

    this.serviceSensor
      .getAssetAlarms(this.assetManager.asset.assetInfo.AssetId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (alarmList: SensorAlarmModel[]) => {
          if (alarmList?.length > 0) {
            this.sensorAlarmMap = {}
            for (const sensorAlarm of alarmList) {
              //console.log(sensorAlarm);
              this.sensorAlarmMap[sensorAlarm.id] = sensorAlarm;
            }
          } else {
            this.sensorAlarmMap = {}
          }
        }
      )

    this.serviceSensor
      .getAssetSensors(this.assetManager.asset.assetInfo.AssetId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (sensorsList: SensorModel[]) => {
          if (sensorsList?.length > 0) {
            this.sensorMap = {}
            for (const sensor of sensorsList){
              this.sensorMap[sensor.id] = sensor;
              this._rtReading[sensor.id] = new Subject<SensorDataPair>();
            }
            this.listSensors = sensorsList

            let keys = ['id', 'label', 'sensorTypeName']

            this.serviceCommon.createStringFilter(this.listSensors, keys).then((list) => {
              this.listSensors = [...list]
              this.updateListSensorsFiltered()
            })
          } else {
            this.sensorMap = {}
            this.notFound = true
          }

          this.loadSensor3DModel().then(() => {
            this.insertAddedSensorsToSensorCampaign()
          })

          //this.connectToIotAlarmHub()
          this.connectToIoTStreamService()

          this.loading = false
        },
        (error) => {
          console.log(error)
          this.loading = false
        },
      )

  }

  private createSensorElement(
    sensor: SensorModel,
    sensorPosition: Vector3,
    target: WorkableElement,
  ): WorkableElement {
    let sensorElement: WorkableElement = null

    if (!this.sensor3dModel) {
      console.error('sensor3dModel is not initialized');
      return null;
    }

    let mesh: Mesh = this.sensor3dModel.clone()
    mesh.geometry.computeBoundingBox()
    mesh.geometry.center()
    mesh.position.set(sensorPosition.x, sensorPosition.y, sensorPosition.z)
    let normal: THREE.Vector3 = target.section.sectionNormal.normalize()
    const canonicalAxis = Util.LargestCanonicalAxis(normal)
    // console.log(normal)
    // console.log(canonicalAxis)
    if (canonicalAxis === 'PositiveZ' || canonicalAxis === 'NegativeZ') {
      //mesh.rotateY(1.5708)
    } else if (canonicalAxis === 'PositiveY' || canonicalAxis === 'NegativeY') {
      mesh.rotateY(1.5708)
      mesh.rotateZ(-1.5708)
    } else if (canonicalAxis === 'PositiveX' || canonicalAxis === 'NegativeX') {
      mesh.rotateX(-1.5708)
      mesh.rotateZ(-1.5708)
    }

    sensorElement = new BasicSensor(
      mesh,
      GraphicsMaterials.sensorMaterial,
      sensor.id,
      target,
      sensor,
    )

    return sensorElement
  }

  private insertSensorMethod(ev, sectionId: string, regionIds: Array<string>) {

    var box = this.assetManager.asset.GetStructureVisibleRegions()[0].sections.find(x => x.Guid === sectionId).box3
    let pointNormal: any = {normal: null};
    let _sensorsGeneral = null
    _sensorsGeneral = this.graphicsRenderDecal.insertPlatePoint(
      ev,
      box,
      this.assetManager.asset,
      sectionId,
      pointNormal
    )

    if (_sensorsGeneral != null) {
      for (const _sensor of _sensorsGeneral) {
        let _target: WorkableElement = null
        for (const structureCollection of this.assetManager.asset.StructureCampaigns) {
          if (this.assetManager.currentSection.Guid) {
            _target = structureCollection.getElementById(
              this.assetManager.currentRegion.Guid,
              this.assetManager.currentSection.Guid,
              _sensor.target.name,
            )
            if (_target) break
          } else {
            _target = structureCollection.getElementByIdOnly(
              this.assetManager.currentRegion.Guid,
              _sensor.target.name,
            )
            if (_target) break
          }
        }

        // const sensorElement = new BasicSensor(_inspectionPoint.point, this.graphicsMaterials.pipeMaterial, this.sensorSelected.id, _target, this.sensorSelected);
        const sensorElement = this.createSensorElement(
          this.sensorSelected,
          _sensor.point.position,
          _target,
        ) as BasicSensor;
        if (sensorElement) {
          this.assetManager.asset.SensorCampaign.addSurfaceElement(sensorElement)
          this.serviceSensor.updateSensor(sensorElement.sensorProperties).subscribe(
            response => {
              console.log(response);
              this.notificationService.addSuccessNotification('Sensor Information Updated')
            },
            error => {
              console.log(error);
              this.notificationService.addFailedNotification('Error Updating Sensor')
            }
          );
        }
      }
      this.updateListSensorsFiltered()
    }
  }

  public insertAddedSensorsToSensorCampaign() {
    this.sensorReadingScale = {};

    // Check if StructureCampaigns exists and is iterable
    if (!this.assetManager.asset.StructureCampaigns || !Array.isArray(this.assetManager.asset.StructureCampaigns)) {
      console.error("StructureCampaigns is not initialized or not iterable.");
      return;  // Exit the method if StructureCampaigns is not iterable
    }

    for (let sensor of this.listSensors) {
      if (sensor.targetId) {
        let target: WorkableElement = null
        for (const structureCollection of this.assetManager.asset.StructureCampaigns) {
          target = structureCollection.getElementByIdOnly(
            sensor.regionId,
            sensor.targetId,
          )
          if (target) break
        }
        if (!target) continue
        let sensorPosition: Vector3 = new Vector3(
          sensor.position[0],
          sensor.position[1],
          sensor.position[2],
        )
        let minReading = -100;
        let maxReading = 100;


        let sensorElement = this.createSensorElement(sensor, sensorPosition, target);
        if (sensorElement) {
          // if (this.sensorAlarmMap[sensor.id]){
          //   (sensorElement as BasicSensor).initCSS3DObject(this.graphics.Camera.up.clone(),
          //   true,
          //   this.sensorAlarmMap[sensor.id].alarmLowerLimit,
          //   this.sensorAlarmMap[sensor.id].alarmUpperLimit);
          // } else{
          //   (sensorElement as BasicSensor).initCSS3DObject(this.graphics.Camera.up.clone(),
          //   false);
          // }
          if (this.sensorAlarmMap[sensor.id]) {
            const minValue = this.sensorAlarmMap[sensor.id].alarmLowerLimit;
            const maxValue = this.sensorAlarmMap[sensor.id].alarmUpperLimit;
            this.sensorReadingScale[sensor.id] = this.resultBarMaxHeight / (1.3 * Math.abs(maxValue - minValue));
            (sensorElement as BasicSensor).showResultBar(GraphicsMaterials.sensorEmptyBarMaterial);
          } else {
            this.sensorReadingScale[sensor.id] = this.resultBarMaxHeight / (1.3 * 100); //since no max or min reference value is given we set this to 100
            (sensorElement as BasicSensor).showResultBar(GraphicsMaterials.sensorEmptyBarMaterial);
          }

          this.assetManager.asset.SensorCampaign.addSurfaceElement(sensorElement)
          this.assetManager.asset.SensorCampaign.addSurfaceCSSElement(sensorElement)
        }

        // const platePoint = this.graphicsRenderDecal.createPlatePointFromPosition(sensor.position, target, 'Plate', '', target.section.sectionNormal.normalize(), target.section.box3);

        // sensorElement = new BasicSensor(platePoint.point, this.graphicsMaterials.pipeMaterial, sensor.id, target, sensor);
        // this.assetManager.asset.SensorCampaign.addSurfaceElement(sensorElement);
      }
    }
  }

  public filterNotAddedSensors(listSensors: SensorModel[]): SensorModel[] {
    return listSensors.filter(
      (sensor) =>
        this.assetManager.asset.SensorCampaign.getElementByIdOnly(
          this.assetManager.currentRegion.Guid,
          sensor.id,
        ) == null,
    )
  }

  public updateListSensorsFiltered(): void {
    this.listSensorsFiltered = this.listSensors
      .filter((sensor) => !sensor.targetId)
      .filter(
        (sensor) =>
          this.assetManager.asset.SensorCampaign.getElementByIdOnly(
            sensor.regionId,
            sensor.id,
          ) == null,
      )
  }

  public async connectToIoTStreamService() {
    const user = await this.storageService.getItem<User>(SESSION_STORAGE.USER)
    this.iotStreamService.startConnection(
      this.assetManager.asset.assetInfo.AssetId,
      user.id,
      'iotrthub',
      this.rawDeviceMsg,
    )

    this.rawDeviceMsg.subscribe((data) => {
      console.log("Received new data from rawDeviceMsg:", data);
      let deviceMsg = JSON.parse(data.payload)

      for (let sensor in deviceMsg.telemetryItems) {
        let senReading = deviceMsg.telemetryItems[sensor].value;
        if (this._rtReading[sensor]) {
          this._rtReading[sensor].next(
          {
            utcTimeStamp: deviceMsg.utcTimeStamp,
            value: senReading
          }
          );
          let sensorElement: WorkableElement =
            this.assetManager.asset.SensorCampaign.getElementByIdOnly(
              this.sensorMap[sensor].regionId,
              sensor
            );
          let barHeight = this.getResultBarHeight(sensor, senReading);
          let barMaterial = this.getResultBarMaterial(sensor, senReading);
          if(sensorElement) {
            (sensorElement as BasicSensor).updateBarPlot(barHeight, barMaterial);

          }
        }
      }
    })
  }

  private getResultBarHeight(sensorId: string, sensorReading: number): number {
    const barScaleFactor = this.sensorReadingScale[sensorId];
    if (barScaleFactor) {
      return Math.min(Math.abs(sensorReading) * barScaleFactor, this.resultBarMaxHeight);
    } else {
      return Math.min(Math.abs(sensorReading), this.resultBarMaxHeight);
    }
  }

  private getResultBarMaterial(sensorId: string, sensorReading: number): THREE.Material {
    const sensorAlarm = this.sensorAlarmMap[sensorId];
    let color;
    if (sensorAlarm) {
      if (sensorReading > sensorAlarm.alarmUpperLimit || sensorReading < sensorAlarm.alarmLowerLimit) {
        color = this.lut.getColor(1);
      } else if (sensorReading > sensorAlarm.normalUpperLimit) {
        const d = 0.5 + 0.5 * (sensorReading - sensorAlarm.normalUpperLimit) / (sensorAlarm.alarmUpperLimit - sensorAlarm.normalUpperLimit);
        color = this.lut.getColor(d);
      } else if (sensorReading < sensorAlarm.normalLowerLimit) {
        const d = 0.5 + 0.5 * (sensorAlarm.normalUpperLimit - sensorReading) / (sensorAlarm.normalLowerLimit - sensorAlarm.alarmLowerLimit);
        color = this.lut.getColor(d);
      } else {
        const dUpper = 0.5 * (sensorAlarm.normalUpperLimit - sensorReading) / sensorAlarm.normalUpperLimit;
        const dLower = 0.5 * (sensorAlarm.normalLowerLimit - sensorReading) / sensorAlarm.normalLowerLimit;
        color = this.lut.getColor(Math.max(dUpper, dLower));
      }
      return GraphicsMaterials.getSensorResultBarMaterial(color)
    } else {
      return GraphicsMaterials.getSensorResultBarMaterial()
    }
  }


  public async connectToIotAlarmHub() {
    const user = await this.storageService.getItem<User>(SESSION_STORAGE.USER)
    this.iotStreamService.startConnection(
      this.assetManager.asset.assetInfo.AssetId,
      user.id,
      'iotalarmhub',
      this.deviceMsg,
    )

    this.deviceMsg.subscribe((data) => {
      this.listSensorStatus = JSON.parse(data.payload)
      // console.log(this.listSensorStatus)

      for (let sensorStatus of this.listSensorStatus) {
        let status = sensorStatus.status
        let sensorId = sensorStatus.id
        let sensorElement: WorkableElement =
          this.assetManager.asset.SensorCampaign.getElementByIdOnly(
            this.assetManager.currentRegion.Guid,
            sensorId,
          )
        if (sensorElement) {
          switch (status) {
            case 'Alarm':
              sensorElement.defaultMaterial = GraphicsMaterials.redMaterial
              break

            case 'Normal':
              sensorElement.defaultMaterial = GraphicsMaterials.greenMaterial
              break

            case 'Warning':
              sensorElement.defaultMaterial = GraphicsMaterials.yellowMaterial
              break

            default:
              sensorElement.defaultMaterial = GraphicsMaterials.pipeMaterial
              break
          }
        }
      }
    })
  }

  public closeIotHubConnection() {
    this.iotStreamService.stopAllConnections()
  }
}
